import { ReactNode } from 'react'
import cn from 'classnames/bind'
import moment from 'moment-timezone'

import PlainDateMonthSwitcher from 'app/common/ui-next/plain-date-month-picker'
import * as dateTypes from 'app/common/constants/dateTypes'
import useFromTo from 'app/common/hooks/useFromTo'
import PlainIcon, { iconTypes } from 'app/common/ui-next/plain-icon'
import { GenerateFile } from 'app/common/ui-components/progress-indicator'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent, Header2 } from 'app/common/templates-next/page-template'
import SimpleDate from 'app/common/classes/simple-date'
import PlainDateSwitcher from 'app/common/ui-next/plain-date-switcher'

import HistoryApi from '../HistoryApi'
import HistoryTable from './HistoryTable'
import useGenerateFileRequests from './useGenerateFileRequests'

import styles from './FinancesHistory.module.scss';

const cx = cn.bind(styles)

type Props = {
  date: SimpleDate
  dateType: 'month' | 'day'
  header?: string
  person: {
    id: number
    name: {
      first: string
      last: string
      middle: string
      gender: 'male' | 'female' | 'androgynous'
    }
    timezone: string
  }
  school: {
    id: number | null
    title: string
    isCamp?: boolean
  }
  bannerPlaceId?: string
  blockDetails?: string
  subNavigation?: ReactNode
  onChangeDate: (date: string, dateFormat: string, daily?: boolean) => void
}

const FinancesHistory = ({
  date,
  dateType: dt,
  header = 'Финансы',
  person: { id: personId, timezone },
  school,
  bannerPlaceId,
  blockDetails,
  subNavigation,
  onChangeDate,
}: Props) => {
  const dateString = date.getStringFormatted(dt === 'day' ? 'YYYY-MM-DD' : 'YYYY-MM')
  const dateType = dt === 'day' ? dateTypes.DAYS : dateTypes.MONTHS // length(split('-', date)) // 2 - мясяцы, 3 - дни

  const [from, to] = useFromTo({ date: dateString, dateType })

  const schoolId = (school.isCamp && school.id) || undefined

  const reportFileName = `student_finances_${moment().format('YYYYMMDD_HHmmss')}`
  const generateFileRequests = useGenerateFileRequests(
    { personId, date: dateString, schoolId },
    reportFileName,
  )

  return (
    <HistoryApi personId={personId} from={from} to={to} schoolId={schoolId}>
      {subNavigation && (
        <Sticky>
          {(ref, sticky) => (
            <PageTemplateContent reference={ref} className={cx(styles.flex, { sticky })}>
              {subNavigation}
            </PageTemplateContent>
          )}
        </Sticky>
      )}

      <Sticky>
        {(ref, sticky) => (
          <PageTemplateContent reference={ref} className={cx(styles.flex, { sticky })}>
            <Header2 skipMargin>{header}</Header2>
            <GenerateFile
              labelAsLink
              label="Скачать отчет"
              generateAndDownloadPrompt="Сгенерировать и скачать в формате «документ Excel»"
              downloadPrompt="Скачать в формате «документ Excel»"
              {...generateFileRequests}
            >
              <PlainIcon width={20} height="auto" type={iconTypes.EXCEL32} />
            </GenerateFile>

            <PlainDateSwitcher
              className={styles.dateSwitcher}
              date={dateString}
              dateFormat={dateType === dateTypes.MONTHS ? 'YYYY-MM' : 'YYYY-MM-DD'}
              displayDateFormat={dateType === dateTypes.MONTHS ? 'MMMM' : undefined}
              stepType={dt}
              onDateChange={onChangeDate}
            >
              <PlainDateMonthSwitcher
                date={dateString}
                dateFormat={dateType === dateTypes.MONTHS ? 'YYYY-MM' : 'YYYY-MM-DD'}
                onChangeDate={onChangeDate}
                hideDateMonthSwitcher
                monthsAmount={24}
              />
            </PlainDateSwitcher>
          </PageTemplateContent>
        )}
      </Sticky>

      <HistoryTable
        timezone={timezone}
        date={dateString}
        bannerPlaceId={bannerPlaceId}
        blockDetails={blockDetails}
      />
    </HistoryApi>
  )
}

export default FinancesHistory
